import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import theme from "../../theme";
import {
    getCountriesAction,
} from "../login/action";
import Loading from "../../components/loading";
import CommonInput from "../../components/input";
import CommonButton from "../../components/button";
import CommonDropDown from "../../components/dropdown";
import {
    onBoardVendorInfoAction,
    onBoardVendorVerifyAction,
} from "./action";

const useStyles = makeStyles(() => ({
    root: {
        margin: "50px 0px",
        maxWidth: "600px",
        margin: "50px auto",
        padding: "0 20px",
    },
    flexContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    logo: { width: "100px", height: "76px" },
    welcomeText: {
        margin: "20px",
        textAlign: "center",
        fontSize: "28px",
        fontFamily: theme.fontFamily.MARKPROLIGHT,
    },
    formContainer: {
        backgroundColor: theme.colors.White,
        boxShadow: "0px 2px 15px #D0CFDB99",
        borderRadius: "15px",
        padding: "40px",
        marginTop: "30px",
    },
    formRow: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px",
        marginBottom: "20px",
        "@media (max-width: 768px)": {
            gridTemplateColumns: "1fr",
        },
    },
    select: {
        marginBottom: "20px",
        "& .MuiOutlinedInput-root": {
            backgroundColor: theme.colors.Zumthor,
            "&.Mui-focused": {
                backgroundColor: theme.colors.White,
            },
        },
    },
    errorContainer: {
        backgroundColor: theme.colors.Cinderella,
        color: theme.colors.Monza,
        padding: "15px",
        borderRadius: "10px",
        marginBottom: "20px",
        fontFamily: theme.fontFamily.MARKPRO,
    },
    successContainer: {
        backgroundColor: theme.colors.MintGreen,
        color: theme.colors.Violet,
        padding: "15px",
        borderRadius: "10px",
        marginBottom: "20px",
        fontFamily: theme.fontFamily.MARKPRO,
        textAlign: "center",
    },
}));

function VendorRegistration({ match }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const getCountriesMethod = useCallback(
        () => dispatch(getCountriesAction()),
        [dispatch]
    );

    const onBoardVendorInfoMethod = useCallback(
        (vid, token) => dispatch(onBoardVendorInfoAction(vid, token)),
        [dispatch]
    );

    const onBoardVendorVerifyMethod = useCallback(
        (data) => dispatch(onBoardVendorVerifyAction(data)),
        [dispatch]
    );

    const loginReducer = useSelector((state) => state.loginReducer);
    const onBoardVendorReducer = useSelector((state) => state.onBoardVendorReducer);

    const [formData, setFormData] = useState({
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        businessName: "",
        businessDescription: "",
        country: "",
        address: "",
        phoneNumber: "",
        startTime: "",
        endTime: "",
        isValidated: false,
    });

    const [phoneError, setPhoneError] = useState("");

    const validatePhoneNumber = (phoneNumber, countryCode) => {
        if (!phoneNumber || !phoneNumber.trim()) {
            return "Phone number may not be blank.";
        }
        if (!countryCode) {
            return "Please select a country first.";
        }
        try {
            const isValid = isValidPhoneNumber(phoneNumber, countryCode);
            if (!isValid) {
                return "Please enter a valid phone number for the selected country.";
            }
            return "";
        } catch (error) {
            return "Please enter a valid phone number.";
        }
    };

    const vid = match?.params?.vid;
    const token = match?.params?.token;

    useEffect(() => {
        getCountriesMethod();
    }, [getCountriesMethod]);

    useEffect(() => {
        if (vid && token) {
            onBoardVendorInfoMethod(vid, token);
        }
    }, [vid, token, onBoardVendorInfoMethod]);

    useEffect(() => {
        const { onBoardVendorInfo, success, error } = onBoardVendorReducer;
        if (error) {
            window.scrollTo(0, 0);
        }
        if (success && !error) {
            // Redirect to login or success page
            setTimeout(() => {
                history.push("/login");
            }, 2000);
        }
        // Pre-fill form if vendor info exists
        if (onBoardVendorInfo) {
            const updates = {};
            if (onBoardVendorInfo.first_name) {
                updates.firstName = onBoardVendorInfo.first_name;
            }
            if (onBoardVendorInfo.last_name) {
                updates.lastName = onBoardVendorInfo.last_name;
            }
            if (onBoardVendorInfo.business_name) {
                updates.businessName = onBoardVendorInfo.business_name;
            }
            if (onBoardVendorInfo.business_description) {
                updates.businessDescription = onBoardVendorInfo.business_description;
            }
            if (onBoardVendorInfo.country) {
                updates.country = onBoardVendorInfo.country;
            }
            if (onBoardVendorInfo.address) {
                updates.address = onBoardVendorInfo.address;
            }
            if (onBoardVendorInfo.phone_number) {
                updates.phoneNumber = onBoardVendorInfo.phone_number;
            }
            if (onBoardVendorInfo.start_time) {
                updates.startTime = onBoardVendorInfo.start_time;
            }
            if (onBoardVendorInfo.end_time) {
                updates.endTime = onBoardVendorInfo.end_time;
            }
            if (Object.keys(updates).length > 0) {
                setFormData((prev) => ({ ...prev, ...updates }));
            }
        }
    }, [onBoardVendorReducer]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormData((prev) => ({ ...prev, isValidated: true }));

        // Validate phone number
        const phoneValidationError = validatePhoneNumber(formData.phoneNumber, formData.country);
        setPhoneError(phoneValidationError);

        if (
            !formData.password ||
            !formData.confirmPassword ||
            !formData.firstName ||
            !formData.lastName ||
            !formData.businessName ||
            !formData.country ||
            !formData.address ||
            !formData.phoneNumber ||
            !formData.startTime ||
            !formData.endTime ||
            formData.password !== formData.confirmPassword ||
            formData.password.length < 8 ||
            phoneValidationError
        ) {
            return;
        }

        const submitData = {
            password: formData.password,
            first_name: formData.firstName,
            last_name: formData.lastName,
            business_name: formData.businessName,
            business_description: formData.businessDescription || "",
            country: formData.country,
            address: formData.address,
            phone_number: formData.phoneNumber,
            start_time: formData.startTime,
            end_time: formData.endTime,
        };

        onBoardVendorVerifyMethod({
            vid,
            token,
            data: submitData,
        });
    };

    const countryOptions = loginReducer?.countryOptions || [];
    const countryDropdownOptions = countryOptions.map((option) => ({
        value: option.id,
        label: option.name,
    }));
    const selectedCountryOption = countryDropdownOptions.find(
        (option) => option.value === formData.country
    ) || null;

    return (
        <div className={classes.root}>
            <Helmet>
                <title>{"Sanaam - Vendor Registration"}</title>
                <meta property="og:title" content={"Sanaam - Vendor Registration"} />
            </Helmet>
            <Loading showLoader={onBoardVendorReducer?.loading || loginReducer?.loading} />

            <div className={classes.flexContainer}>
                <img className={classes.logo} src="/images/logo.svg" alt="logo"></img>
            </div>
            <div className={`${classes.flexContainer} ${classes.welcomeText}`}>
                Welcome! Complete your vendor registration
            </div>

            <div className={classes.formContainer}>
                {onBoardVendorReducer?.error?.length ? (
                    <div className={classes.errorContainer}>
                        {Array.isArray(onBoardVendorReducer.error)
                            ? onBoardVendorReducer.error.map((each, index) => (
                                <div key={index}>{each}</div>
                            ))
                            : <div>{onBoardVendorReducer.error}</div>}
                    </div>
                ) : null}

                {onBoardVendorReducer?.success ? (
                    <div className={classes.successContainer}>
                        Registration successful! Redirecting to login...
                    </div>
                ) : null}

                <form onSubmit={handleSubmit} noValidate>
                    <CommonInput
                        label="Password"
                        type="password"
                        placeholder="Enter password (min 8 characters)"
                        value={formData.password}
                        onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                        error={(!formData.password || formData.password.length < 8)}
                        helperText={

                            ((!formData.password && "Password may not be blank.") ||
                                (formData.password && formData.password.length < 8 && "Password must be at least 8 characters."))
                        }
                    />

                    <CommonInput
                        label="Confirm Password"
                        type="password"
                        placeholder="Confirm password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                        error={(!formData.confirmPassword || formData.password !== formData.confirmPassword)}
                        helperText={
                            ((!formData.confirmPassword && "Please confirm your password.") ||
                                (formData.confirmPassword && formData.password !== formData.confirmPassword && "Passwords do not match."))
                        }
                    />

                    <div className={classes.formRow}>
                        <CommonInput
                            label="First Name"
                            placeholder="Enter first name"
                            value={formData.firstName}
                            onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                            error={!formData.firstName}
                            helperText={!formData.firstName && "First name may not be blank."}
                        />

                        <CommonInput
                            label="Last Name"
                            placeholder="Enter last name"
                            value={formData.lastName}
                            onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                            error={!formData.lastName}
                            helperText={!formData.lastName && "Last name may not be blank."}
                        />
                    </div>

                    <CommonInput
                        label="Business Name"
                        placeholder="Enter business name"
                        value={formData.businessName}
                        onChange={(e) => setFormData((prev) => ({ ...prev, businessName: e.target.value }))}
                        error={!formData.businessName}
                        helperText={!formData.businessName && "Business name may not be blank."}
                    />

                    <CommonInput
                        label="Business Description"
                        placeholder="Enter business description (optional)"
                        value={formData.businessDescription}
                        onChange={(e) => setFormData((prev) => ({ ...prev, businessDescription: e.target.value }))}
                        multiline
                        rows={3}
                    />

                    <div className={classes.select}>
                        <label
                            style={{
                                fontSize: "16px",
                                color: theme.colors.Violet,
                                marginBottom: "5px",
                                fontFamily: theme.fontFamily.MARKPROMEDIUM,
                                display: "block",
                            }}
                        >
                            Country
                        </label>
                        <CommonDropDown
                            placeholder="Select Country"
                            options={countryDropdownOptions}
                            value={selectedCountryOption}
                            onChange={(selectedOption) => {
                                const countryValue = selectedOption ? selectedOption.value : "";
                                setFormData((prev) => ({ ...prev, country: countryValue }));
                                // Re-validate phone number when country changes
                                if (formData.phoneNumber) {
                                    const error = validatePhoneNumber(formData.phoneNumber, countryValue);
                                    setPhoneError(error);
                                }
                            }}
                            error={!!phoneError}
                            helperText={phoneError}
                            borderRadius="4px"
                        />
                        {formData.isValidated && !formData.country && (
                            <div
                                style={{
                                    color: "red",
                                    fontSize: "12px",
                                    marginTop: "5px",
                                    fontFamily: theme.fontFamily.MARKPRO,
                                }}
                            >
                                Country may not be blank.
                            </div>
                        )}
                    </div>

                    <CommonInput
                        label="Address"
                        placeholder="Enter business address"
                        value={formData.address}
                        onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                        error={!formData.address}
                        helperText={!formData.address && "Address may not be blank."}
                    />

                    <CommonInput
                        label="Phone Number"
                        placeholder="Enter phone number"
                        value={formData.phoneNumber}
                        type="tel"
                        onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, phoneNumber: value }));
                            // Validate in real-time onChange
                            const error = validatePhoneNumber(value, formData.country);
                            setPhoneError(error);
                        }}
                        error={!!phoneError}
                        helperText={phoneError}
                    />

                    <div className={classes.formRow}>
                        <CommonInput
                            label="Open Time"
                            type="time"
                            value={formData.startTime}
                            onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))}
                            error={!formData.startTime}
                            helperText={!formData.startTime && "Opening time may not be blank."}
                        />

                        <CommonInput
                            label="Close Time"
                            type="time"
                            value={formData.endTime}
                            onChange={(e) => setFormData((prev) => ({ ...prev, endTime: e.target.value }))}
                            error={!formData.endTime}
                            helperText={!formData.endTime && "Closing time may not be blank."}
                        />
                    </div>

                    <CommonButton
                        type="submit"
                        text="Complete Registration"
                        stylevariant="primary"
                        style={{ marginTop: "20px", width: "100%" }}
                        disabled={
                            !formData.password ||
                            !formData.confirmPassword ||
                            !formData.firstName ||
                            !formData.lastName ||
                            !formData.businessName ||
                            !formData.country ||
                            !formData.address ||
                            !formData.phoneNumber ||
                            !formData.startTime ||
                            !formData.endTime ||
                            formData.password !== formData.confirmPassword ||
                            formData.password.length < 8 ||
                            !!phoneError
                        }
                    />
                </form>
            </div>
        </div>
    );
}

export default VendorRegistration;
